import { ModelBase } from '../../core/model/model-base';
import { Address } from '../../common/model/address';
import { Entrance } from './entrance';
import { Area } from './area';
import { BuildingSpace } from './building-space';
import { BuildingDocument } from './building-document';
import { PostTargetModel } from '../service/base/post-target-loader.interface';
import { LinkedCustomProperty } from './linked-custom-property';
import { LocalDate } from '../../core/model/local-date';

export class Building extends ModelBase {
  constructor(dataContext?: any) {
    super();
    if (dataContext) {
      this.id = dataContext.id;
      this.address = Object.assign(new Address(), dataContext.address);
      this.areaDescription = dataContext.areaDescription;
      this.buildingDescription = dataContext.buildingDescription;
      this.entrances = Array.isArray(dataContext.entrances) ? dataContext.entrances.map(ent => Object.assign(new Entrance(), ent)) : [];
      this.areas = Array.isArray(dataContext.areas) ? dataContext.areas.map(ent => Object.assign(new Area(), ent)) : [];
      this.buildingCustomProperties = Array.isArray(dataContext.buildingCustomProperties)
        ? dataContext.buildingCustomProperties.map(ent => Object.assign(new LinkedCustomProperty(), ent))
        : [];
      this.buildingCustomPropertiesTextValues = dataContext.buildingCustomPropertiesTextValues || {};
      this.buildingCustomPropertiesDateValues = dataContext.buildingCustomPropertiesDateValues || {};
      this.buildingCustomPropertiesNumericValues = dataContext.buildingCustomPropertiesNumericValues || {};
      this.buildingCustomPropertiesBooleanValues = dataContext.buildingCustomPropertiesBooleanValues || {};
      this.buildingSpaces = Array.isArray(dataContext.buildingSpaces) ? dataContext.buildingSpaces.map(s => new BuildingSpace(s)) : [];
      this.buildYear = dataContext.buildYear;
      this.costPlace = dataContext.costPlace;
      this.costBearer = dataContext.costBearer;
      this.centralLocation = dataContext.centralLocation;
      this.floorsInBuilding = dataContext.floorsInBuilding;
      this.name = dataContext.name;
      this.note = dataContext.note;
      this.number = dataContext.number;
      this.reconstructionYear = dataContext.reconstructionYear;
      this.property = dataContext.property;
      this.propertyName = dataContext.propertyName;
      this.companyName = dataContext.companyName;
      this.bra = dataContext.bra;
      this.boa = dataContext.boa;
      this.loa = dataContext.loa;
      this.bta = dataContext.bta;
      this.atemp = dataContext.atemp;
      this.agar = dataContext.agar;
      this.nta = dataContext.nta;
      this.ova = dataContext.ova;
      this.bia = dataContext.bia;
      this.latitude = dataContext.latitude;
      this.longitude = dataContext.longitude;
      this.dhMarketArea = dataContext.dhMarketArea;
      this.firstPartOfNote = dataContext.firstPartOfNote;
      this.documents = dataContext.documents || [];
      this.lettingShowingOrdinary = dataContext.lettingShowingOrdinary;
    } else {
      this.address = new Address();
      this.entrances = [];
      this.areas = [];
      this.buildingCustomProperties = [];
      this.buildingCustomPropertiesTextValues = {};
      this.buildingCustomPropertiesDateValues = {};
      this.buildingCustomPropertiesNumericValues = {};
      this.buildingCustomPropertiesBooleanValues = {};
      this.documents = [];
    }
  }

  address: Address;
  areaDescription: string;
  buildingDescription: string;
  costPlace: string;
  costBearer: string;
  entrances: Entrance[];
  areas: Area[];
  buildingCustomProperties: LinkedCustomProperty[];
  buildingCustomPropertiesTextValues: Record<string, string>;
  buildingCustomPropertiesDateValues: Record<string, LocalDate>;
  buildingCustomPropertiesNumericValues: Record<string, number>;
  buildingCustomPropertiesBooleanValues: Record<string, boolean>;
  buildYear: number;
  centralLocation: string;
  floorsInBuilding: number;
  latitude: number;
  longitude: number;
  name: string;
  note: string;
  firstPartOfNote: string;
  number: string;
  reconstructionYear: number;
  property: string;
  propertyName: string;
  companyName: string;
  bra: number;
  boa: number;
  loa: number;
  bta: number;
  atemp: number;
  agar: number;
  nta: number;
  bia: number;
  ova: number;
  dhMarketArea: string;
  buildingSpaces: BuildingSpace[];
  documents: BuildingDocument[];
  lettingShowingOrdinary: string;

  toString() {
    return `${this.number} ${this.name}`;
  }
}

export class BuildingPostTarget extends ModelBase implements PostTargetModel {
  constructor(dataContext?: BuildingPostTarget) {
    super();
    if (dataContext) {
      this.id = dataContext.id || '';
      this.propertyNumberName = dataContext.propertyNumberName || '';
      this.companyNumberName = dataContext.companyNumberName || '';
      this.buildingNumberName = dataContext.buildingNumberName || '';
    }
  }

  buildingNumberName: string;
  companyNumberName: string;
  propertyNumberName: string;

  postTargetDescription() {
    return {
      key: 'POST.POST_TARGET_DESCRIPTION.BUILDING',
      params: { companyName: this.companyNumberName, propertyName: this.propertyNumberName, buildingName: this.buildingNumberName },
    };
  }

  toAutocompleteValue(viewType: 'list' | 'field', culture?: string): string {
    switch (viewType) {
      case 'field':
      case 'list':
        return this.buildingNumberName;
    }
  }

  toString(culture?: string): string {
    return this.buildingNumberName;
  }
}
